import {
  useDeferredDismiss,
  type ActionCardSurface,
} from '@littlebird/action-cards';
import { observer } from 'mobx-react-lite';
import { motion, useReducedMotion } from 'motion/react';

import {
  CardFallback,
  CardV2,
  useCardRendering,
} from '@/features/action-cards-view';
import { AskUserActionCardBody } from '@/features/ask-user/AskUserActionCardBody';
import { cn } from '@/lib/utils';

import { trackDismissal } from '../analytics';
import { surfaceOutcome } from '../commands';
import { useDismissToast } from '../use-dismiss-toast';

import { ChatFeedCard } from './ChatFeedCard';

import type { ActionCard, ActionCardDismissal } from '@littlebird/sdk';
import type { ReactNode } from 'react';

export type CardV2ItemLayout = 'chat' | 'list';

type CardV2ItemProps = {
  card: ActionCard;
  sourceChatTag?: ReactNode;
  surface?: ActionCardSurface;
  onAskUserComplete?: () => void;
  openInCardChat?: (seed?: string) => void;
  onDismiss?: (dismissal?: ActionCardDismissal) => void;
  /** Off for a pane that swaps one card for the next, which shows each card at once */
  animateIn?: boolean;
  layout?: CardV2ItemLayout;
};

export const CardV2Item = observer(
  ({
    card,
    surface = 'chat',
    sourceChatTag,
    onAskUserComplete,
    openInCardChat,
    onDismiss,
    animateIn = true,
    layout = 'chat',
  }: CardV2ItemProps) => {
    // Views arrive by sync on the same MobX card, so React Compiler must not cache this
    'use no memo';
    const deferredDismiss = useDeferredDismiss({
      onEvent: trackDismissal(surface),
      onOutcome: surfaceOutcome,
    });
    // The card folds to its own Restore, so the toast only asks why
    useDismissToast(deferredDismiss, 'chat');
    const reducedMotion = useReducedMotion();
    const rendering = useCardRendering(card, 'web_chat');
    const body =
      card.actionType === 'ask_user' ? (
        <AskUserActionCardBody card={card} onComplete={onAskUserComplete} />
      ) : undefined;
    const showsFallback = body === undefined && rendering === 'fallback';

    // Span, not div: this card also renders inside a paragraph
    return (
      <motion.span
        className={cn('block w-full', layout === 'chat' && 'not-prose')}
        initial={reducedMotion || !animateIn ? false : { opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reducedMotion ? 0 : 0.18, ease: 'easeOut' }}
      >
        {sourceChatTag ? (
          <span className="flex shrink-0 flex-col items-end gap-1">
            {sourceChatTag}
          </span>
        ) : null}
        {showsFallback ? (
          <CardFallback card={card} surface="web_chat" />
        ) : body !== undefined ? (
          <CardV2
            card={card}
            body={body}
            surface="web_chat"
            deferredDismiss={deferredDismiss}
            reportedSurface={surface}
          />
        ) : (
          <ChatFeedCard
            card={card}
            reportedSurface={surface}
            deferredDismiss={deferredDismiss}
            openInCardChat={openInCardChat}
            onDismiss={onDismiss}
            layout={layout}
          />
        )}
      </motion.span>
    );
  }
);
